import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { QUADRATIC_META, standardToVertex, vertexToStandard, type QuadraticParams } from './quadratic.model';

/** Quadratic params with index signature for JsxParamStore compatibility */
type QuadraticParamsIndexed = QuadraticParams & { [key: string]: number };

export class QuadraticCreator extends BaseFunctionCreator<QuadraticParamsIndexed> {
  readonly functionType = 'quadratic';
  readonly defaultParams: QuadraticParamsIndexed = { ...QUADRATIC_META.defaultParams };
  readonly availableFeatures = QUADRATIC_META.availableFeatures;
  readonly handleConfigs = QUADRATIC_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;

    // Normalize input params - accepts either vertex form (a,h,k) or standard form (a,b,c)
    const initialParams = this.normalizeParams(def.params);

    // Get stroke color for handles (needs to match curve color)
    const colorName = (display?.curve?.color ?? 'sky') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE - stores all 5 params (a, h, k, b, c)
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: QUADRATIC_META.defaultRanges.a },
      h: { value: initialParams.h, range: QUADRATIC_META.defaultRanges.h },
      k: { value: initialParams.k, range: QUADRATIC_META.defaultRanges.k },
      b: { value: initialParams.b, range: QUADRATIC_META.defaultRanges.b },
      c: { value: initialParams.c, range: QUADRATIC_META.defaultRanges.c },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION (uses vertex form internally)
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      return a * Math.pow(x - h, 2) + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          return 2 * a * (x - h);
        };
      }
      if (order === 2) {
        return () => 2 * paramStore.getValue('a');
      }
      return () => 0;
    };

    // ------------------------------------------------
    // BIDIRECTIONAL PARAM SYNC
    // ------------------------------------------------

    /**
     * Set params with automatic sync between vertex and standard forms.
     *
     * When vertex form params (h, k) change → update standard form (b, c)
     * When standard form params (b, c) change → update vertex form (h, k)
     * When 'a' changes → update the non-incoming form
     *
     * Key: Compute ALL values first, then apply in a single setParams() call.
     * This ensures sync callbacks run with correct values for handle positioning.
     */
    const setParamsWithSync = (incoming: Partial<Record<string, number>>): void => {
      const updatingVertex = 'h' in incoming || 'k' in incoming;
      const updatingStandard = 'b' in incoming || 'c' in incoming;

      // Merge incoming with current params (use bracket notation for index signature)
      const current = paramStore.getParams();
      const merged = { ...current, ...incoming };
      const a = merged['a']!;

      if (updatingStandard && !updatingVertex) {
        // Standard form changed → compute vertex form, then apply all
        const { h, k } = standardToVertex(a, merged['b']!, merged['c']!);
        paramStore.setParams({ ...incoming, h, k });
      } else {
        // Vertex form changed (or 'a' only, or both) → compute standard form, then apply all
        const { b, c } = vertexToStandard(a, merged['h']!, merged['k']!);
        paramStore.setParams({ ...incoming, b, c });
      }
    };

    return {
      id,
      type: 'quadratic',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as QuadraticParamsIndexed,
      setParams: setParamsWithSync,
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }

  /**
   * Normalize input params - accepts either vertex form (a,h,k) or standard form (a,b,c).
   * Computes the missing representation to ensure all 5 params are initialized.
   */
  private normalizeParams(params?: Record<string, number>): QuadraticParamsIndexed {
    const base = { ...this.defaultParams };

    if (!params) {
      return base;
    }

    // Merge provided params
    const merged = { ...base, ...params };

    // Determine which form was provided
    const hasVertex = 'h' in params || 'k' in params;
    const hasStandard = 'b' in params || 'c' in params;

    if (hasStandard && !hasVertex) {
      // Standard form provided → compute vertex form
      const { h, k } = standardToVertex(merged.a, merged.b, merged.c);
      merged.h = h;
      merged.k = k;
    } else {
      // Vertex form provided (or both, or neither) → compute standard form
      const { b, c } = vertexToStandard(merged.a, merged.h, merged.k);
      merged.b = b;
      merged.c = c;
    }

    return merged as QuadraticParamsIndexed;
  }
}
